import FormFieldCalendar from '@/components/FormFieldCalendar';
import FormFieldComponent from '@/components/FormFieldComponent';
import ToolBar from '@/components/ToolBar';
import { Badge } from '@/components/ui/badge';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { BillingItem, InvoiceBillingInformationTable } from './InvoiceBillingInformationTable';
import { useEffect, useState } from 'react';
import { CircleCheckBig, Flag, SquareCheckBig, SquarePen } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useGetInvoiceId, useUpdateInvoiceMutation } from '@/services/invoiceService';
import { displayFullName, formatCurrency, genUniqueId } from '@/lib/utils';
import { InvoiceFormSchema, InvoiceFormSchemaType } from './InvoiceSchema';
import { InvoiceStatus, InvoiceUpdateSchemaType } from '@/types/invoice.types';
import AlertBox from '@/components/AlertBox';
import Loader from '@/components/Loader';
import { NotFoundRedirectionWrapper } from '@/components/NotFoundRedirectWrapper';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { useUserStore } from '@/stores/user';
import { zodResolver } from '@hookform/resolvers/zod';
import { hasFeature } from '@/services/featureService';
import { isSupervisor } from '@/services/userService';
import ApplyPaymentModal from '@/components/ApplyPaymentModal';
import { isAdminLikeRole } from '@/lib/roles';

export const invoiceStatus = {
  PAID: {
    label: 'Paid',
    fontColor: '#1B8652',
    backgroundColor: '#D5F4E3',
  },
  REVIEWED: {
    label: 'Reviewed',
    fontColor: '#6C4A09',
    backgroundColor: '#FFEEBF',
  },
  BILLED: {
    label: 'Reviewed',
    fontColor: '#6C4A09',
    backgroundColor: '#FFEEBF',
  },
  CONFIRMED: {
    label: 'Confirmed',
    fontColor: '#FBEDFF',
    backgroundColor: '#551175',
  },
  DRAFT: {
    label: 'Draft',
    fontColor: '#0A2240',
    backgroundColor: '#C0CBD9',
  },
};

export const InvoiceStatusBadge = ({
  status,
}: {
  status: { label: string; fontColor: string; backgroundColor: string };
}) => {
  return (
    <Badge
      className={`hover:bg-[${status.backgroundColor}]`}
      style={{
        color: status.fontColor,
        backgroundColor: status.backgroundColor,
      }}
    >
      {status.label}
    </Badge>
  );
};

export const InvoiceView = ({ mode, allowEdit = true }: { mode: 'view' | 'edit'; allowEdit?: boolean }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParam] = useSearchParams();

  const form = useForm<InvoiceFormSchemaType>({ mode: 'onBlur', resolver: zodResolver(InvoiceFormSchema) });

  const updateInvoice = useUpdateInvoiceMutation();
  const { control, reset, setValue } = form;
  const { isValid } = form.formState;
  const status = useWatch({ control, name: 'status' });

  const userStore = useUserStore();
  const [refreshKey, setRefreshKey] = useState(0);
  const disabledStyleClass = '!border-[#CAC8C8] !bg-[#F5F5F5] !opacity-100';
  const { data, isLoading, isError } = useGetInvoiceId(id, refreshKey);

  const [billingItems, setBillingItems] = useState<BillingItem[]>([]);
  const [isAlertBoxOpen, setIsAlertBoxOpen] = useState(false);
  const [workOrderId, setWorkOrderId] = useState<string | null>(null);
  const hasApplyPaymentFeature = hasFeature('accounting.applyPayment');
  const [openApplyPaymentModal, setOpenApplyPaymentModal] = useState(false);

  useEffect(() => {
    reset({
      invoiceId: data?.invoiceNumber ?? '',
      clientName: data?.workOrder?.service?.client?.clientName ?? '',
      toServe: data?.workOrder?.toServe ?? '',
      attorney: displayFullName(data?.workOrder?.service?.case?.attorney),
      contact: displayFullName(data?.workOrder?.service?.contact),
      status: data?.status ?? InvoiceStatus.DRAFT,
      billingDate: data?.billDate ? new Date(data.billDate) : undefined,
      caseName: data?.workOrder?.service?.case?.name ?? '',
    });

    setBillingItems(
      data?.invoiceBillingItems?.map((item) => ({
        id: genUniqueId(),
        itemId: item.itemId ?? '',
        description: item.billingItem?.description ?? '',
        amount: Number(item.amount) || 0,
        billingItemId: item.id,
      })) ?? []
    );
  }, [data]);

  useEffect(() => {
    const totalAmount =
      billingItems.reduce((acc, item) => {
        const itemAmount = item.amount || 0;
        return acc + itemAmount;
      }, 0) ?? 0;
    setValue('amount', billingItems.length ? formatCurrency(totalAmount ?? 0) : formatCurrency(0));
  }, [billingItems]);

  const saveHandler = (status?: InvoiceStatus) => {
    const updateData: Partial<InvoiceUpdateSchemaType> = {};

    const originalBillingItemIds = data?.invoiceBillingItems?.map((item) => item.id) ?? [];

    const currentBillingItemIds = billingItems.filter((item) => item.billingItemId).map((item) => item.billingItemId);

    const billingItemsToCreate: { itemId: string; amount: number }[] = [];
    const billingItemsToUpdate: { billingItemId: string; amount: number }[] = [];
    const billingItemsToDelete: string[] = [];
    billingItems.forEach((item) => {
      if (!item.billingItemId || item.billingItemId === '') {
        billingItemsToCreate.push({
          itemId: item.itemId,
          amount: Number(item.amount),
        });
      } else {
        const originalItem = data?.invoiceBillingItems?.find((orig) => orig.id === item.billingItemId);
        if (originalItem && Math.abs(Number(originalItem.amount) - Number(item.amount)) > 0) {
          billingItemsToUpdate.push({
            billingItemId: item.billingItemId,
            amount: Number(item.amount),
          });
        }
      }
    });

    originalBillingItemIds.forEach((originalId) => {
      if (originalId && !currentBillingItemIds.includes(originalId)) {
        billingItemsToDelete.push(originalId);
      }
    });

    updateData.billingItems = {
      ...(billingItemsToCreate.length > 0 && { create: billingItemsToCreate }),
      ...(billingItemsToUpdate.length > 0 && { update: billingItemsToUpdate }),
      ...(billingItemsToDelete.length > 0 && { delete: billingItemsToDelete }),
    };
    if (status && status !== data?.status) {
      updateData.status = status;
    }
    if (!updateData?.billingItems?.create && !updateData.billingItems?.update && !updateData.billingItems?.delete) {
      delete updateData.billingItems;
    }

    updateInvoice.mutate(
      {
        invoiceNumber: data?.invoiceNumber ?? '',
        data: updateData,
      },
      {
        onSuccess: (data) => {
          if (mode === 'edit') navigate(`/process/invoice/${id}${workOrderId ? `?workOrderId=${workOrderId}` : ''}`);
          toast({
            description: data.message || 'Invoice updated successfully',
          });
          setIsAlertBoxOpen(false);
          setRefreshKey((prev) => prev + 1);
        },
        onError: (error) => {
          displayError(error || 'Failed to update invoice');
        },
      }
    );
  };

  useEffect(() => {
    const searchParamWoId = searchParam.get('workOrderId');
    setWorkOrderId(searchParamWoId);
  }, []);

  return (
    <NotFoundRedirectionWrapper itemName="Invoice" isItemNotFound={isError}>
      <Loader className="w-full scrollbar-hide flex gap-6 flex-col" isLoading={isLoading}>
        <div className="w-full flex flex-row gap-2">
          {data?.workOrder?.service?.case?.rush && (
            <div className="text-error h-5 w-5 pt-2" title="Rush Case">
              <Flag className="text-[#D72222] w-4 h-4" fill="#D72222" />
            </div>
          )}
          <ToolBar
            title={`W/O: ${data?.workOrder?.number} - Invoice`}
            secondaryTitle={<InvoiceStatusBadge key={status} status={invoiceStatus[status ?? 'DRAFT']} />}
            additionalButton={
              <div className="flex flex-row gap-6 items-center">
                {mode === 'view' && (
                  <>
                    {allowEdit && (
                      <div
                        key="edit"
                        className="border-[1px] border-[#CAC8C8] p-1 h-[32px] w-[32px] rounded-md cursor-pointer"
                        onClick={() => navigate(`update${workOrderId ? `?workOrderId=${workOrderId}` : ''}`)}
                      >
                        <SquarePen className="text-placeholder" />
                      </div>
                    )}

                    {[InvoiceStatus.REVIEWED, InvoiceStatus.DRAFT, invoiceStatus.BILLED].includes(status) &&
                      (['ADMIN', 'OPERATOR'].includes(userStore.user?.roleId ?? '') ||
                        isSupervisor(userStore.user)) && (
                        <Loader isLoading={updateInvoice.status === 'pending'}>
                          <Button
                            variant="outline"
                            className="h-[36px]"
                            onClick={() => {
                              saveHandler(InvoiceStatus.CONFIRMED);
                            }}
                          >
                            <div className="flex flex-row gap-3 items-center">
                              <SquareCheckBig className="h-4 w-4" />
                              Mark as Confirmed
                            </div>
                          </Button>
                        </Loader>
                      )}
                    {hasApplyPaymentFeature &&
                      status === InvoiceStatus.CONFIRMED &&
                      (isAdminLikeRole(userStore.user?.roleId) || userStore.user?.roleId === 'ACCOUNTANT') && (
                        <Button
                          className="h-[36px] bg-[#1B8652] hover:bg-[#1B8652]"
                          onClick={() => {
                            setOpenApplyPaymentModal(true);
                          }}
                          key={refreshKey}
                        >
                          <div className="flex flex-row gap-3 items-center">
                            <CircleCheckBig className="h-4 w-4" />
                            Apply Payment
                          </div>
                        </Button>
                      )}
                  </>
                )}
              </div>
            }
          />
        </div>
        <FormProvider {...form}>
          <div className="grid grid-cols-12 gap-6 w-full">
            <div className="col-span-12 md:col-span-6 space-y-4">
              <FormFieldComponent
                name="invoiceId"
                control={control}
                disabled
                label="Invoice #"
                defaultValue={data?.invoiceNumber ?? ''}
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="clientName"
                control={control}
                disabled
                defaultValue={data?.workOrder?.service?.client?.clientName ?? ''}
                label="Client Name"
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="caseName"
                control={control}
                disabled
                defaultValue={data?.workOrder?.service?.case?.name ?? ''}
                label="Case Name"
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="toServe"
                control={control}
                disabled
                label="Person Served"
                defaultValue={data?.workOrder?.toServe ?? ''}
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="attorney"
                control={control}
                disabled
                label="Attorney Name"
                disabledStyle={disabledStyleClass}
              />
              <FormFieldComponent
                name="contact"
                control={control}
                disabled
                label="Contact Name"
                disabledStyle={disabledStyleClass}
              />
            </div>

            <div className="col-span-12 md:col-span-6 border-[1px] border-[#CAC8C8] h-full rounded-md">
              <div className="bg-[#f5f5f5] rounded-md p-6 font-bold">Billing Information</div>
              <div>
                <InvoiceBillingInformationTable
                  serviceType={data?.workOrder?.service?.type ?? null}
                  department={data?.workOrder?.vendor?.department ?? null}
                  mode={mode}
                  billingItems={billingItems}
                  setBillingItems={setBillingItems}
                  key={`invoice-billing-information-table-${billingItems.length}`}
                />
              </div>
            </div>
            <div className="col-span-6 md:col-span-6 space-y-4">
              <FormFieldCalendar
                name="billingDate"
                control={control}
                label="Billing Date"
                disabled
                disabledStyle={disabledStyleClass}
                placeholder="Invoice not confirmed"
              />
            </div>
            <div className="col-span-6 md:col-span-6 space-y-4">
              <FormFieldComponent
                name="amount"
                control={control}
                disabled
                label="Total Billing Amount"
                disabledStyle={disabledStyleClass}
              />
            </div>
          </div>
        </FormProvider>

        {openApplyPaymentModal && (
          <ApplyPaymentModal
            open={openApplyPaymentModal}
            onOpenChange={setOpenApplyPaymentModal}
            invoices={
              data?.invoiceNumber
                ? [
                    {
                      invoiceNumber: data.invoiceNumber,
                      invoiceAmount: data.totalAmount ?? 0,
                      balance: data.balance ?? 0,
                    },
                  ]
                : []
            }
            onSuccess={(paymentData) => {
              setOpenApplyPaymentModal(false);
              const remainingBalance = paymentData.invoices[0]?.balance ?? 0;
              if (remainingBalance <= 0) {
                saveHandler(InvoiceStatus.PAID);
              } else {
                setRefreshKey((prev) => prev + 1);
              }
            }}
          />
        )}

        {mode === 'edit' && (
          <div className="flex flex-row gap-3 w-full justify-end pb-6 pt-10">
            <Button
              variant="outline"
              className="h-[36px] w-[112px]"
              onClick={() => navigate(`/process${workOrderId ? `?workOrderId=${workOrderId}` : ''}`)}
            >
              Cancel
            </Button>
            <AlertBox
              triggerButton={
                <Button
                  variant="default"
                  className="h-[36px] w-[103px] hover:bg-[#12375C]"
                  disabled={!isValid || Object.keys(form.formState.dirtyFields).length === 0}
                  onClick={() => setIsAlertBoxOpen(true)}
                  data-testid="trigger-save-button"
                >
                  Save
                </Button>
              }
              title={`Are you sure?`}
              description={`Would you like to update invoice details?`}
              cancelBtnLabel="No"
              confirmBtnLabel="Yes"
              open={isAlertBoxOpen}
              isLoading={updateInvoice.status === 'pending'}
              cancelBtnClick={() => setIsAlertBoxOpen(false)}
              confirmBtnClick={() => saveHandler()}
            />
          </div>
        )}
      </Loader>
    </NotFoundRedirectionWrapper>
  );
};
